/**
 * CJProductsService — Appel CJ DIRECT depuis l'app
 * Company: 7931766 | Publisher: 101729219
 * React Native n'a pas de CORS, on peut appeler CJ directement
 */
import { AWS_CONFIG } from '../config/aws_config';

const CJ_TOKEN   = AWS_CONFIG.cjToken     || '1LZH7LlDz6I6eISKA9rXMxxr7g';
const CJ_COMPANY = AWS_CONFIG.cjCompanyId || '7931766';

export const ADVERTISERS = {
  cashmere: '1989180',
  iqueens:  '7133205',
};

// Limite CJ : max 1000 par requête
const CJ_LIMIT = 500;

// ─── Fetch CJ direct ─ une requête ─────────────────────────────
async function fetchCJOnce(partner, keywords = '') {
  const advertiserId = ADVERTISERS[partner];
  if (!advertiserId) return [];

  const kwParam = keywords ? `,keywords:"${keywords}"` : '';
  const query = `{shoppingProducts(companyId:"${CJ_COMPANY}",partnerIds:["${advertiserId}"]${kwParam},limit:${CJ_LIMIT}){resultList{id advertiserId title description price{amount currency} salePrice{amount currency} imageLink link brand productType} totalCount}}`;

  try {
    const res = await fetch('https://ads.api.cj.com/query', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${CJ_TOKEN}`,
        'Accept': 'application/json',
        'User-Agent': 'OutfitApp/1.0',
      },
      body: JSON.stringify({ query }),
    });
    const text = await res.text();
    if (!res.ok) {
      console.warn(`[CJ ${keywords || 'no-kw'}] HTTP ${res.status}:`, text.slice(0, 100));
      return [];
    }
    const data = JSON.parse(text);
    if (data.errors) {
      console.warn(`[CJ ${keywords || 'no-kw'}] GraphQL errors:`, JSON.stringify(data.errors).slice(0, 150));
      return [];
    }
    const products = data.data?.shoppingProducts?.resultList || [];
    console.log(`[CJ ${partner} "${keywords || '∅'}"] → ${products.length} produits`);
    // Tag chaque produit avec la query source pour le filtrage gender ultérieur
    return products.map(p => ({ ...p, _cjQuery: keywords }));
  } catch(e) {
    console.warn(`[CJ ${keywords || 'no-kw'}] Error:`, e.message);
    return [];
  }
}

// ─── Fetch CJ multi-catégorie ──────────────────────────────────
// Pour iQueens : on fait plusieurs requêtes avec des mots-clés différents
// pour couvrir un maximum de catégories (robes, sacs, accessoires, etc.)
async function fetchFromCJ(partner, gender) {
  if (partner === 'cashmere') {
    // ═══ DÉSACTIVÉ : mode iQueens uniquement ═══
    return [];
  }

  if (partner === 'iqueens') {
    // iQueens : multi-requêtes pour couvrir toutes les catégories
    const genderKw = gender === 'M' ? 'homme men' : 'femme women';
    
    const queries = gender === 'M' ? [
      genderKw,                                    // général
      'homme chaussures shoes',                    // chaussures (base iQueens)
      'homme sac bag backpack',                    // sacs
      'homme accessoire chapeau bonnet montre',    // accessoires
      'homme veste jacket manteau',                // vestes
      'homme pantalon jean',                       // bas
      'homme bijou collier bracelet',              // bijoux hommes
      'homme t-shirt polo chemise',                // hauts
      'homme sweat hoodie pull pullover',          // hauts mailles
      'homme shirt',                               // hauts variantes (sans blouse)
    ] : [
      genderKw,                                    // général
      'femme robe dress',                          // robes
      'femme chaussures shoes sneaker',            // chaussures
      'femme sac bag backpack',                    // sacs
      'femme accessoire chapeau montre lunette',   // accessoires
      'femme veste jacket manteau',                // vestes
      'femme pantalon jean jupe',                  // bas
      'femme bijou collier bracelet bague',        // bijoux (nouveau !)
      'femme boucle oreille earring necklace',     // bijoux suite
      'femme t-shirt top tshirt',                  // hauts top ← NEW
      'femme blouse chemisier chemise shirt',      // hauts blouses ← NEW
      'femme sweat hoodie pull pullover cardigan', // hauts mailles ← NEW
      'femme crop top bustier debardeur',          // hauts lifestyle ← NEW
    ];

    // Lancer toutes les requêtes en parallèle
    const results = await Promise.all(queries.map(kw => fetchCJOnce(partner, kw)));
    
    // Dédoublonner par ID
    const seen = new Set();
    const merged = [];
    for (const products of results) {
      for (const p of products) {
        const key = p.id || p.title;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(p);
      }
    }
    console.log(`[CJ iqueens] Total dédoublonné: ${merged.length} produits`);
    return merged;
  }

  return [];
}

// ─── Mapper produit CJ → format Outfit ───────────────────────
// Lien Evergreen par advertiser pour wrapper URLs produits avec tracking CJ
const CJ_DEEP_LINK_IDS = {
  cashmere: '10471851',  // Promo 50% off Cashmere
  iqueens:  '15830678',  // Evergreen iQueens
};
const CJ_PUB = AWS_CONFIG.cjPublisherId || '101729219';

function wrapWithCJTracking(rawUrl, partner) {
  if (!rawUrl) return rawUrl;
  // Si déjà un lien CJ (kqzyfj, anrdoezrs, jdoqocy, tkqlhce...) : laisser tel quel
  if (/kqzyfj|anrdoezrs|jdoqocy|tkqlhce|lduhtrp|dpbolvw/i.test(rawUrl)) {
    return rawUrl;
  }
  const linkId = CJ_DEEP_LINK_IDS[partner];
  if (!linkId) return rawUrl;
  // ═══ Format CJ Deep Link Generator (recommandé par CJ depuis 2023) ═══
  // Format : https://www.anrdoezrs.net/click-PUB-LINK?url=ENCODED_URL&sid=SID
  // Le domaine alterne (anrdoezrs.net, kqzyfj.com, jdoqocy.com, etc.) selon
  // le serving CJ. On utilise anrdoezrs.net qui reste valide et stable.
  // Le SID sera ajouté plus tard par injectSidInUrl si tracking actif.
  return `https://www.anrdoezrs.net/click-${CJ_PUB}-${linkId}?url=${encodeURIComponent(rawUrl)}`;
}

// Construit un lien de recherche sur le site marchand avec tracking CJ
function buildSearchUrl(partner, productName) {
  // Pas d'encodeURIComponent ici — wrapWithCJTracking va encoder toute l'URL
  // On garde seulement les caractères compatibles URL (espaces deviennent +)
  const q = productName.slice(0, 60).replace(/[^\w\s-]/g, '').replace(/\s+/g, '+');
  let searchUrl;
  if (partner === 'cashmere') {
    searchUrl = `https://www.cashmereboutique.com/?s=${q}`;
  } else if (partner === 'iqueens') {
    searchUrl = `https://www.iqueens.fr/fr/recherche?controller=search&s=${q}`;
  } else {
    return null;
  }
  return wrapWithCJTracking(searchUrl, partner);
}

// ─── Détection catégorie multi-source (nom + description + productType) ───
// Scoring : chaque source contribue, on prend la catégorie qui gagne.
// Source CJ `productType` = champ Google Product Category (très fiable quand présent).
function detectCategory(name, description, productType, partner) {
  const t = ((name || '') + ' ' + (description || '') + ' ' + (productType || '')).toLowerCase();

  // Patterns par catégorie. Chaque pattern matché ajoute des points.
  // Plus le pattern est spécifique, plus il pèse.
  const patterns = {
    Robes: [
      [/\brobes?\b|\bdress(es)?\b/, 5],
      [/\bjupes?\b|\bskirt(s)?\b/, 4],
      [/\bcombinaison\b|\bjumpsuit\b|\boveralls?\b/, 4],
      [/maxi.dress|midi.dress|mini.dress/, 5],
    ],
    Vestes: [
      [/\bvestes?\b|\bjacket(s)?\b|\bcoat(s)?\b|\bmanteau(x)?\b/, 5],
      [/\bblouson\b|\bparka\b|\bdoudoune\b|\btrench\b|\bblazer\b/, 5],
      [/\bgilet\b|\bcardigan\b/, 4],
      [/\bouterwear\b|\boutdoor.coat/, 4],
    ],
    Bas: [
      [/\bjeans?\b|\bdenim\b/, 5],
      [/\bpantalons?\b|\bpant(s)?\b|\btrouser(s)?\b/, 5],
      [/\bleggings?\b|\bjegging(s)?\b/, 5],
      [/\bshort(s)?\b|\bbermuda(s)?\b/, 5],
      [/\bjogger(s)?\b|\bjogging\b|\bsweatpant(s)?\b/, 4],
      [/\bchino(s)?\b|\bcargo\b/, 4],
    ],
    Hauts: [
      [/\bt-shirts?\b|\btee.shirts?\b|\btee(s)?\b/, 5],
      [/\bpolo(s)?\b|\bchemises?\b|\bshirt(s)?\b/, 5],
      [/\bblouses?\b|\bchemisier(s)?\b/, 5],
      [/\bsweat(s)?\b|\bhoodie(s)?\b|\bsweatshirt(s)?\b/, 5],
      [/\bpull(s)?\b|\bpullover(s)?\b|\bjumper(s)?\b|\bsweater(s)?\b/, 5],
      [/\btops?\b/, 3],
      [/\bcrop.top\b|\bbustier\b|\bdebardeur\b|\btank.top\b/, 5],
      [/\btunic(s)?\b|\bmaillot\b/, 4],
    ],
    Chaussures: [
      [/\bsneakers?\b|\bbasket(s)?\b|\btrainer(s)?\b/, 5],
      [/\bchaussures?\b|\bshoe(s)?\b/, 5],
      [/\bbottes?\b|\bbottines?\b|\bboot(s)?\b|\bbootie(s)?\b/, 5],
      [/\bmocassin(s)?\b|\bderby\b|\bloafer(s)?\b/, 5],
      [/\bescarpins?\b|\bpump(s)?\b|\bheel(s)?\b/, 5],
      [/\bsandales?\b|\bsandal(s)?\b|\btong(s)?\b|\bflip.flop(s)?\b/, 5],
      [/\bespadrille(s)?\b|\bmule(s)?\b|\bbabouche(s)?\b/, 4],
      [/footwear/, 4],
    ],
    Sacs: [
      [/\bsacs?\b|\bbag(s)?\b/, 5],
      [/\bpochette(s)?\b|\bclutch(es)?\b|\bpouch(es)?\b/, 5],
      [/\bcartable(s)?\b|\bbackpack(s)?\b|\brucksack(s)?\b/, 5],
      [/\bcabas\b|\btote\b|\btote.bag\b/, 5],
      [/\bbesace(s)?\b|\bcrossbody\b|\bshoulder.bag\b/, 5],
      [/\bportefeuille(s)?\b|\bwallet(s)?\b/, 4],
      [/handbag(s)?|purse(s)?/, 5],
    ],
    Bijoux: [
      [/\bbijoux?\b|\bjewelry\b|\bjewellery\b/, 5],
      [/\bcollier(s)?\b|\bnecklace(s)?\b|\bchaîne(s)?\b|\bchain(s)?\b/, 5],
      [/\bbracelet(s)?\b|\bbangle(s)?\b/, 5],
      [/\bbagues?\b|\bring(s)?\b/, 5],
      [/\bboucles?.d.oreille(s)?\b|\bearring(s)?\b/, 5],
      [/\bpendentif(s)?\b|\bpendant(s)?\b/, 4],
    ],
    Accessoires: [
      [/\bécharpe(s)?\b|\bscarf\b|\bscarves\b|\bfoulard(s)?\b|\bpashmina\b|\bshawl(s)?\b/, 5],
      [/\bchapeau(x)?\b|\bhat(s)?\b|\bcap(s)?\b|\bcasquette(s)?\b|\bbeanie(s)?\b|\bbonnet(s)?\b/, 5],
      [/\bceinture(s)?\b|\bbelt(s)?\b/, 5],
      [/\bgants?\b|\bglove(s)?\b|\bmittens?\b/, 5],
      [/\blunettes?\b|\bsunglasses\b|\bglasses\b/, 5],
      [/\bmontre(s)?\b|\bwatch(es)?\b/, 5],
      [/\bcravate(s)?\b|\bnoeud.papillon\b|\btie(s)?\b|\bbow.tie\b/, 5],
      [/accessor(y|ies|ies)/, 3],
    ],
  };

  // Scoring : on additionne les points par catégorie
  const scores = {};
  for (const [cat, patList] of Object.entries(patterns)) {
    let total = 0;
    for (const [regex, weight] of patList) {
      if (regex.test(t)) total += weight;
    }
    if (total > 0) scores[cat] = total;
  }

  // Catégorie gagnante = score max. En cas d'égalité, on privilégie l'ordre de
  // probabilité réelle des produits CJ : Hauts > Bas > Chaussures > Vestes >
  // Robes > Sacs > Accessoires > Bijoux
  const fallbackOrder = ['Hauts', 'Bas', 'Chaussures', 'Vestes', 'Robes', 'Sacs', 'Accessoires', 'Bijoux'];
  if (Object.keys(scores).length === 0) {
    // Aucun pattern matché → fallback
    return partner === 'cashmere' ? 'Hauts' : 'Hauts';
  }
  const maxScore = Math.max(...Object.values(scores));
  const winners = Object.entries(scores).filter(([_, s]) => s === maxScore).map(([c]) => c);
  if (winners.length === 1) return winners[0];
  // Tie-break par fallback order
  for (const c of fallbackOrder) if (winners.includes(c)) return c;
  return winners[0];
}

function mapProduct(item, partner, gender) {
  const name  = item.title || item.name || '';
  // CJ retourne price/salePrice comme objet { amount, currency }
  const priceRaw = item.salePrice?.amount ?? item.price?.amount ?? item.salePrice ?? item.price ?? 0;
  const origRaw  = item.price?.amount ?? item.price ?? 0;
  const price = parseFloat(priceRaw);
  const orig  = parseFloat(origRaw);
  let image = item.imageLink || item.imageUrl || item.image_link || '';
  let rawUrl = item.link || item.buyUrl || item.buy_url || '';

  // Forcer HTTPS pour les images (React Native bloque HTTP par défaut)
  if (image && image.startsWith('http://')) {
    image = 'https://' + image.slice(7);
  }

  // iQueens : proxy systématique de TOUTES les images via wsrv.nl.
  // Certaines images sont hébergées sur static.qns.digital (bloquent le hotlink
  // sans Referer iqueens.fr), d'autres directement chez les marques
  // (ex: Calvin Klein, Guess) qui bloquent aussi. React Native ne peut pas
  // envoyer de Referer custom → on passe TOUT par wsrv.nl qui fetch côté serveur.
  if (partner === 'iqueens' && image && image.startsWith('http')) {
    // Éviter le double-proxy si déjà wrappé
    if (!image.includes('wsrv.nl') && !image.includes('weserv.nl')) {
      image = 'https://wsrv.nl/?url=' + encodeURIComponent(image) + '&w=600';
    }
  }

  // Nettoyer le paramètre ?size= pour iQueens qui peut bloquer la page
  if (partner === 'iqueens' && rawUrl.includes('?size=')) {
    rawUrl = rawUrl.split('?size=')[0];
  }

  // Pour Cashmere : les images du catalogue CJ sont majoritairement obsolètes.
  // Stratégie : utiliser notre Lambda /img-proxy qui fetch la page produit LIVE
  // depuis cashmereboutique.com et parse l'og:image (toujours à jour 2023+).
  //   → Images 100% réelles et fraîches, aucun fallback/statique.
  if (partner === 'cashmere' && rawUrl && rawUrl.includes('cashmereboutique.com')) {
    const LAMBDA = 'https://on0ym6si4l.execute-api.eu-west-3.amazonaws.com/prod';
    image = `${LAMBDA}/img-proxy?url=${encodeURIComponent(rawUrl)}&img=${encodeURIComponent(image || '')}`;
  } else if (partner === 'cashmere' && image && image.startsWith('http')) {
    // Edge case : URL produit ne contient pas cashmereboutique.com
    // Fallback : RocketCDN + wsrv.nl
    if (image.includes('cashmereboutique.com')) {
      image = image.replace(/(?:www\.)?cashmereboutique\.com/g, '876572ee.delivery.rocketcdn.me');
    }
    if (!image.includes('wsrv.nl') && !image.includes('/img-proxy')) {
      image = 'https://wsrv.nl/?url=' + encodeURIComponent(image) + '&w=600&output=webp';
    }
  }

  // Wrapper avec tracking CJ affiliation
  const trackedUrl = wrapWithCJTracking(rawUrl, partner);
  // URL de recherche en fallback si la page produit 404
  const searchUrl  = buildSearchUrl(partner, name);

  if (!name || !price || !image || !trackedUrl) return null;

  const disc = orig > price && orig > 0 ? Math.round((1 - price / orig) * 100) : 0;
  const n    = name.toLowerCase();

  let cat = detectCategory(name, item.description, item.productType, partner);

  // Détection stricte du genre à partir du nom/description/brand + query source
  // Retourne : 'M' | 'F' | null (unknown)
  const txt = (name + ' ' + (item.description || '') + ' ' + (item.brand || '')).toLowerCase();
  const hasMale   = /\bmen\b|\bmens\b|\bman\b|\bmale\b|\bhomme\b|\bhommes\b|\bgentleman\b|\bguy\b|\bboys?\b|\bgarcons?\b|\bpour homme\b|\bfor men\b|\bh\/f\b/.test(txt);
  const hasFemale = /\bwomen\b|\bwomens?\b|\bwoman\b|\bfemale\b|\bfemme\b|\bfemmes\b|\blady\b|\bladies\b|\bgirls?\b|\bfilles?\b|\bpour femme\b|\bfor women\b/.test(txt);

  // Patterns "coupe" qui indiquent le genre (robe/jupe = femme uniquement, blazer = souvent homme)
  const femOnlyItems = /\brobe\b|\bdress\b|\bjupe\b|\bskirt\b|\bleggings?\b|\bblouse\b|\bchemisier\b|\bcrop\s*top\b|\bbustier\b|\bcombinaison\b|\btop\s+dos\s+nu\b|\btutu\b/.test(txt);
  const maleOnlyItems = /\bboxer\b|\bslip\b|\bcravate\b|\bnoeud\s*papillon\b|\bbermuda\s+homme\b/.test(txt);

  // Signal depuis la query CJ source ("femme t-shirt" → femme)
  const cjQuery = (item._cjQuery || '').toLowerCase();
  const queryMale   = /\bhomme\b|\bmen\b|\bmale\b/.test(cjQuery);
  const queryFemale = /\bfemme\b|\bwomen\b|\bwoman\b|\blady\b|\bfemale\b/.test(cjQuery);

  let genderGuess;
  if      (hasMale   && !hasFemale)                    genderGuess = 'M';
  else if (hasFemale && !hasMale)                      genderGuess = 'F';
  else if (femOnlyItems)                               genderGuess = 'F';
  else if (maleOnlyItems)                              genderGuess = 'M';
  else if (queryFemale && !queryMale)                  genderGuess = 'F';  // query source fiable
  else if (queryMale && !queryFemale)                  genderGuess = 'M';
  else                                                 genderGuess = null;

  // Si le produit est ambigu, on fait confiance au gender demandé
  // Sinon on impose le vrai genre détecté (empêche les "Men's sneaker" d'arriver dans un fetch femme)
  const finalGender = genderGuess || gender;

  // ═══ STRICT REJECT : si le nom contient clairement l'autre genre, on refuse ═══
  // Ex: fetch 'F' mais produit contient "Men's" ou "Homme" non ambigu → return null
  if (gender && genderGuess && genderGuess !== gender) {
    return null;  // Sera filtré par .filter(Boolean) en amont
  }

  // ═══ RENFORCEMENT GLOBAL ═══
  // Pour les catégories où le risque de mismatch est important (Hauts, Bas,
  // Chaussures, Vestes), si on ne peut pas confirmer le genre avec un signal
  // fort, on REJETTE le produit. Limite : on garde si la marque est typique
  // du genre demandé.
  // Catégories non sensibles : Robes (femme exclusif), Sacs et Accessoires
  // (souvent unisex dans la mode actuelle) → on accepte l'ambiguïté.
  const sensitiveCats = ['Hauts', 'Bas', 'Chaussures', 'Vestes'];
  const isSensitive = sensitiveCats.includes(cat);

  if (isSensitive && genderGuess !== gender) {
    // genderGuess null = aucun signal → on regarde la query CJ + la marque
    if (gender === 'F') {
      const hasFemSignal = hasFemale || queryFemale || femOnlyItems;
      if (!hasFemSignal) {
        const brandLc = (item.brand || '').toLowerCase();
        const femBrands = ['guess', 'karl lagerfeld', 'michael kors', 'calvin klein jeans',
                           'tommy jeans', 'liu jo', 'elisabetta franchi', 'pinko',
                           'twinset', 'patrizia pepe', 'silvian heach', 'rinascimento',
                           'maje', 'sandro', 'iro', 'ba&sh', 'zadig', 'isabel marant'];
        const isFemBrand = femBrands.some(b => brandLc.includes(b));
        if (!isFemBrand) return null; // ambigu pour user femme → reject
      }
    } else if (gender === 'M') {
      const hasMaleSignal = hasMale || queryMale || maleOnlyItems;
      if (!hasMaleSignal) {
        const brandLc = (item.brand || '').toLowerCase();
        const maleBrands = ['hugo boss', 'dsquared2', 'lyle scott',
                            'stone island', 'carhartt wip', 'paul smith',
                            'levi\'s mens', 'diesel homme', 'ralph lauren mens',
                            'tommy hilfiger mens'];
        const isMaleBrand = maleBrands.some(b => brandLc.includes(b));
        if (!isMaleBrand) return null; // ambigu pour user homme → reject
      }
    }
  }

  const IQ_BRANDS = ['Nike','Adidas','New Balance','Vans','Veja','Asics','Puma','Reebok',
                     'The North Face','Dr. Martens','Converse','Vagabond','Michael Kors',
                     'EA7','Birkenstock','Timberland','UGG','Steve Madden','Calvin Klein'];
  const brand = item.brand
    || (partner === 'iqueens' ? IQ_BRANDS.find(b => n.includes(b.toLowerCase())) : null)
    || (partner === 'cashmere' ? 'Cashmere Boutique' : 'iQueens');

  // Cashmere : catalogue CJ contient URLs produits obsolètes (404 sur le site)
  //   → on redirige vers la recherche interne Cashmere avec le nom du produit (tracking CJ conservé)
  //   → le client arrive sur une page de résultats et peut cliquer sur les vrais produits
  // iQueens : catalogue frais → URL produit directe avec tracking CJ
  const finalUrl = partner === 'cashmere' ? searchUrl : trackedUrl;

  // ID unique : même si CJ ne fournit pas d'id, on génère depuis partner + slug URL + prix
  const urlSlug = rawUrl ? rawUrl.split('/').slice(-2).join('_').replace(/[^a-z0-9_]/gi, '').slice(0, 40) : '';
  const uniqueId = item.id
    ? `cj_${partner}_${item.id}`
    : `cj_${partner}_${urlSlug}_${Math.round(price * 100)}`;

  return {
    id:            uniqueId,
    partner,
    brand,
    gender:        finalGender,
    name,
    category:      cat,
    price,
    originalPrice: orig || price,
    discount:      disc || null,
    promoBadge:    disc > 0 ? `-${disc}%` : null,
    emoji:         cat === 'Chaussures' ? '👟' : cat === 'Vestes' ? '🧥' : cat === 'Bas' ? '👗' : cat === 'Accessoires' ? '🧣' : '🧥',
    color:         '',
    image,
    url:           finalUrl,       // lien CJ tracké direct
    originalUrl:   rawUrl || trackedUrl,
    affiliateUrl:  finalUrl,
    searchUrlFallback: searchUrl,   // fallback si URL directe 404
    available:     true,
    rating:        partner === 'cashmere' ? 4.8 : 4.6,
    deliveryDays:  partner === 'cashmere' ? '3-5 j' : '2-4 j',
    sizes:         cat === 'Chaussures'
                     ? (finalGender === 'M' ? ['40','41','42','43','44','45'] : ['36','37','38','39','40','41'])
                     : cat === 'Accessoires' ? ['Unique']
                     : ['XS','S','M','L','XL'],
    isRealProduct: true,
  };
}

// ─── API publique ─────────────────────────────────────────────
export async function getCJCashmereProducts(gender) {
  try {
    const items  = await fetchFromCJ('cashmere', gender);
    const mapped = items.map(i => mapProduct(i, 'cashmere', gender)).filter(Boolean);
    return mapped;
  } catch(e) {
    console.warn('[CJ Cashmere]', e.message);
    return [];
  }
}

export async function getCJIQueensProducts(gender) {
  // v1.10.73 : DÉSACTIVÉ. La DB Lambda est désormais la SEULE source de
  // vérité pour iqueens. Cet appel CJ direct depuis le frontend cause
  // des erreurs F/M car :
  //   - Récupère ~880 produits non vérifiés
  //   - Le mapping côté frontend n'a pas accès au champ description.gender
  //   - Cause des produits homme dans les comptes femme et inversement
  // Désactivation à la source pour bloquer même si un appelant oublie.
  console.log('[CJ] iQueens : désactivé (utiliser DB Lambda via getCatalog)');
  return [];
}

// Ancienne implémentation (non utilisée, gardée pour ref)
async function _legacyGetCJIQueensProducts(gender) {
  try {
    const items  = await fetchFromCJ('iqueens', gender);
    const mapped = items.map(i => mapProduct(i, 'iqueens', gender)).filter(Boolean);
    return mapped;
  } catch(e) {
    console.warn('[CJ iQueens]', e.message);
    return [];
  }
}

/**
 * Envoie une impression CJ pour un advertiser donné.
 * CJ exige des impressions régulières pour valider les clics. Sans impressions,
 * leurs algorithmes anti-fraude tendent à ignorer les clics.
 *
 * Format CJ image pixel : https://www.lduhtrp.net/image-PUB-LINK
 * (ou https://www.tqlkg.com/image-PUB-LINK selon serving)
 *
 * @param {string} linkId - le linkId CJ de l'advertiser (10471851 pour cashmere, etc.)
 */
export function pingCJImpression(linkId = '10471851') {
  const pub = AWS_CONFIG.cjPublisherId || '101729219';
  const url = `https://www.lduhtrp.net/image-${pub}-${linkId}`;
  fetch(url, { method: 'GET', mode: 'no-cors' })
    .then(() => console.log(`[CJ] impression sent: link=${linkId}`))
    .catch(() => {});
}

/**
 * Envoie des impressions CJ pour tous les advertisers connus.
 * À appeler au cold start de l'app et quand le catalogue affiche des produits.
 */
export function pingAllCJImpressions() {
  pingCJImpression('10471851'); // Cashmere Boutique
  pingCJImpression('15830678'); // iQueens
}
